import React, { useEffect, useState } from "react";
import { DataTable, Column } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { Edit, Image, Star } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Amenity } from "@/types/amenity";
import AmenityDeleteButton from "@/components/pages/amenities/components/AmenityDeleteButton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  areAllSelected,
  isSomeSelected,
  toggleAllOnPage,
  toggleRow,
} from "@/components/pages/amenities/utils/amenitySelection";
import { Switch } from "@/components/ui/switch";
import { useUpdateAmenity } from "@/components/pages/amenities/hooks/amenities.hooks";
import { formatServicesCount } from "@/components/pages/amenities/utils/amenityServicesLabel";
import { useTranslation } from "react-i18next";
import {
  amenityViewPath,
  shouldNavigateFromRow,
} from "@/components/pages/amenities/utils/amenityNavigation";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isRapidPlan } from "@/utils/accessControl";
import {
  getAmenityAccess,
  type AmenityAccess,
} from "@/components/pages/amenities/utils/amenityAccess";
import {
  canToggleHomeStar,
  getHomeStarLabelKey,
} from "@/components/pages/amenities/utils/amenityHomeRow";
import { cn } from "@/lib/utils";

type SortOrder = "asc" | "desc" | null;

type Props = {
  data: Amenity[];
  isLoading: boolean;
  sortBy: string | null;
  sortOrder: SortOrder;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectionResetKey?: number;
  access?: AmenityAccess;
};

const AmenitiesTable: React.FC<Props> = ({
  data,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  selectionResetKey = 0,
  access = getAmenityAccess(true),
}) => {
  const { mutate: updateAmenity, isPending: isUpdating } = useUpdateAmenity();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userProfile } = useAuthenticatedUser();
  const showServices = !isRapidPlan(userProfile);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const pageIds = data.map((amenity) => amenity.id);
  let headerChecked: boolean | "indeterminate" = false;
  if (areAllSelected(selectedIds, pageIds)) headerChecked = true;
  else if (isSomeSelected(selectedIds, pageIds))
    headerChecked = "indeterminate";
  const pageKey = pageIds.join(",");

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, pageKey, selectionResetKey]);

  const columns: Column<Amenity>[] = [
    {
      key: "select",
      header: (
        <Checkbox
          className="size-5 border-zinc-900"
          checked={headerChecked}
          onCheckedChange={() =>
            setSelectedIds((prev) => toggleAllOnPage(prev, pageIds))
          }
          aria-label={t("amenities.table.selectAll")}
        />
      ),
      className: "text-center w-10",
      render: (_, row) => (
        <Checkbox
          className="size-5 border-zinc-900"
          checked={selectedIds.includes(row.id)}
          onCheckedChange={() =>
            setSelectedIds((prev) => toggleRow(prev, row.id))
          }
          onClick={(e) => e.stopPropagation()}
          aria-label={t("amenities.table.selectRow", { name: row.name })}
        />
      ),
    },
    {
      key: "isActive",
      header: t("amenities.table.status"),
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center justify-center gap-2">
          <Switch
            checked={row.isActive}
            onCheckedChange={(val) =>
              updateAmenity({ id: row.id, data: { isActive: val } })
            }
            disabled={isUpdating || !access.canToggleStatus}
          />
        </div>
      ),
    },
    {
      key: "photoId",
      header: t("amenities.table.photo"),
      className: "text-center",
      render: (_, row) => (
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded border border-gray-300 bg-gray-100">
          <PresignedImageWithPlaceholder
            className={"h-10 w-10 overflow-hidden object-cover"}
            photo={row.photoId || undefined}
            placeholder={<Image className="h-6 w-6 text-gray-400" />}
          />
        </div>
      ),
    },
    {
      key: "showOnHome",
      header: t("amenities.table.home"),
      className: "text-center w-16",
      render: (_, row) => {
        const starred = !!row.showOnHome;
        const starLabel = t(getHomeStarLabelKey(row), { name: row.name });
        return (
          <span
            className="inline-flex"
            title={starLabel}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="new_ghost"
              size="icon_sm"
              aria-label={starLabel}
              aria-pressed={starred}
              disabled={isUpdating || !canToggleHomeStar(row, access.canEdit)}
              onClick={() =>
                updateAmenity({ id: row.id, data: { showOnHome: !starred } })
              }
              className={cn(
                starred ? "text-royal-glow-3" : "text-muted-foreground",
              )}
            >
              <Star fill={starred ? "currentColor" : "none"} />
            </Button>
          </span>
        );
      },
    },
    {
      key: "name",
      header: t("amenities.table.name"),
      sortable: true,
      className: "text-center",
    },
    {
      key: "category",
      header: t("amenities.table.category"),
      sortable: true,
      className: "text-center",
    },
    {
      key: "description",
      header: t("amenities.table.description"),
      sortable: true,
      className: "text-center",
      render: (value) => (
        <span
          className="line-clamp-2 max-w-[400px] text-left break-words whitespace-normal"
          title={typeof value === "string" ? value : undefined}
        >
          {value}
        </span>
      ),
    },
    ...(showServices
      ? [
          {
            key: "services",
            header: t("amenities.table.services"),
            sortable: true,
            className: "text-center",
            render: (_: unknown, row: Amenity) => {
              const label = formatServicesCount(row.services?.length);
              return <span title={label.title}>{label.text}</span>;
            },
          },
        ]
      : []),
    {
      key: "actions",
      header: t("amenities.table.actions"),
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-1">
          {access.canEdit && (
            <Button
              asChild
              variant="new_ghost"
              size="icon_sm"
              title={t("amenities.table.edit")}
            >
              <Link to={`/amenities/${row.id}`}>
                <Edit />
              </Link>
            </Button>
          )}
          {access.canDelete && <AmenityDeleteButton amenityId={row.id} />}
        </div>
      ),
    },
  ];

  return (
    <DataTable<Amenity>
      data={data}
      getRowKey={(row) => row.id}
      columns={columns}
      loading={isLoading}
      sorting={{
        field: sortBy ?? null,
        direction: sortOrder ?? null,
        onSort,
      }}
      pagination={{
        currentPage,
        totalPages: totalPages || 1,
        onPageChange,
      }}
      onRowClick={(row, _index, event) => {
        if (
          shouldNavigateFromRow(event.currentTarget, event.target as Element)
        ) {
          navigate(amenityViewPath(row.id));
        }
      }}
      onRowKeyActivate={(row) => navigate(amenityViewPath(row.id))}
      withPagination={true}
      withSorting={true}
      emptyMessage={t("amenities.table.empty")}
    />
  );
};

export default AmenitiesTable;
